// Submodule path, not the payments barrel: that barrel re-exports every payment screen, and the
// shell's bell needs one type and one colour table from it.
import { BOLETO_NOTICE_TONE, type BoletoNotice } from '@/features/payments/boletoNotices';

import type { InactivityNotice } from './contracts';
import { noticeCopy, type NoticeTone } from './noticeCopy';

/** Where a notice came from — and therefore which API closes or deletes it. */
export type BellSource = 'inactivity' | 'boleto';

export type BellAction =
  | { readonly kind: 'navigate'; readonly href: string }
  | { readonly kind: 'request_reactivation'; readonly establishmentId: string };

/**
 * One notice of the bell and the floating warning, whatever produced it. The card draws only this,
 * so a new source (the boleto reminders were the second) is an adapter below, not a new component.
 */
export interface BellItem {
  /** Unique across sources: `inactivity:<id>`, `boleto:<id>`. */
  readonly key: string;
  readonly source: BellSource;
  readonly id: string;
  readonly tone: NoticeTone;
  readonly glyph: string;
  readonly label: string;
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string | null;
  readonly action: BellAction | null;
  readonly createdAt: string;
  readonly dismissedToday: boolean;
  /** Lower is more urgent: what floats on entry, and what the bell lists first among equals. */
  readonly urgency: number;
}

const INACTIVITY_URGENCY: Readonly<Record<InactivityNotice['kind'], number>> = {
  blocked: 0,
  last_day: 1,
  final_warning: 1,
  first_warning: 2,
  reactivated: 3,
};

export function fromInactivity(notice: InactivityNotice): BellItem {
  const copy = noticeCopy(notice);
  return {
    key: `inactivity:${notice.id}`,
    source: 'inactivity',
    id: notice.id,
    tone: copy.tone,
    glyph: copy.glyph,
    label: copy.label,
    title: copy.title,
    description: copy.description,
    actionLabel: copy.actionLabel,
    action:
      copy.action === 'request_reactivation'
        ? { kind: 'request_reactivation', establishmentId: notice.establishment_id }
        : { kind: 'navigate', href: '/catalogo' },
    createdAt: notice.created_at,
    dismissedToday: notice.dismissed_today,
    urgency: INACTIVITY_URGENCY[notice.kind],
  };
}

/** The boleto session's four colours, named as the inactivity component names them. */
const BOLETO_TONE: Readonly<Record<string, NoticeTone>> = {
  amber: 'warning',
  red: 'danger',
  graphite: 'inverse',
  green: 'success',
};

const BOLETO_GLYPH: Readonly<Record<BoletoNotice['stage'], string>> = {
  'boleto-issued': '✓',
  'boleto-due-soon': '!',
  'boleto-due-today': '!!',
  'boleto-overdue': '!!',
};

const BOLETO_URGENCY: Readonly<Record<BoletoNotice['stage'], number>> = {
  'boleto-overdue': 1,
  'boleto-due-today': 1,
  'boleto-due-soon': 2,
  'boleto-issued': 3,
};

/** The boleto reminders arrive already worded by the API; only the colour and glyph are ours. */
export function fromBoleto(notice: BoletoNotice): BellItem {
  return {
    key: `boleto:${notice.id}`,
    source: 'boleto',
    id: notice.id,
    tone: BOLETO_TONE[BOLETO_NOTICE_TONE[notice.stage]] ?? 'warning',
    glyph: BOLETO_GLYPH[notice.stage],
    label: notice.eyebrow,
    title: notice.title,
    description: notice.body,
    actionLabel: notice.action_label,
    action: { kind: 'navigate', href: notice.action_href },
    createdAt: notice.created_at,
    dismissedToday: notice.dismissed_today,
    urgency: BOLETO_URGENCY[notice.stage],
  };
}

/** Newest first, as each API lists its own. */
export function mergeBellItems(...lists: readonly (readonly BellItem[])[]): BellItem[] {
  return lists.flat().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * What floats on entry: the most urgent item not closed today — a block before a last warning,
 * a last warning or an overdue boleto before a first warning, good news last.
 */
export function itemToFloat(items: readonly BellItem[]): BellItem | undefined {
  return [...items].filter((item) => !item.dismissedToday).sort((a, b) => a.urgency - b.urgency)[0];
}
